import React from "react";
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FileArrowUp, Sparkle } from "@phosphor-icons/react";
import RichTextEditor from "../../../ISSB/IssbDataInput/components/RichTextEditor/RichTextEditor";
import { FIELD_TYPES } from "../../suppliersMock";

// Lime-outline pill action used in section headers (Import from Template /
// AI Mapping), matching the carbon data-entry section actions.
const SectionAction = ({ icon, children, onClick }) => (
  <Button
    size="sm"
    variant="outline"
    borderRadius="full"
    borderColor="limeShade.400"
    color="limeShade.700"
    fontWeight="600"
    leftIcon={icon}
    _hover={{ bg: "lightLime", borderColor: "limeShade.500" }}
    onClick={onClick}
  >
    {children}
  </Button>
);

const LIME_FOCUS = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

// Renders a single task field per its schema entry. `value` + `onChange` are
// controlled by the parent (the data-type tab). Rich text reuses the shared
// AIR TipTap editor so the toolbar matches the Disclosure tab.
// Sensible default input widths by type, so fields are sized to their content
// rather than spanning the viewport. A field can override with `width`:
// "sm" | "md" | "lg" | "full" (or any Chakra maxW value).
const WIDTH_PRESETS = { sm: "160px", md: "360px", lg: "560px", full: "100%" };
const DEFAULT_WIDTH_BY_TYPE = {
  [FIELD_TYPES.TEXT]: "lg",
  [FIELD_TYPES.TEXTAREA]: "full",
  [FIELD_TYPES.NUMBER]: "sm",
  [FIELD_TYPES.SELECT]: "md",
  [FIELD_TYPES.RADIO]: "full",
  [FIELD_TYPES.CHECKBOX_GROUP]: "full",
  [FIELD_TYPES.CONFIRM]: "full",
  [FIELD_TYPES.RICH_TEXT]: "full",
};

function TaskField({ field, value, onChange }) {
  const { type, label, required, help, unit, options, placeholder, columns, checkboxLabel, width } = field;
  const widthKey = width || DEFAULT_WIDTH_BY_TYPE[type] || "full";
  const controlMaxW = WIDTH_PRESETS[widthKey] || widthKey;

  const control = (() => {
    switch (type) {
      case FIELD_TYPES.TEXT:
        return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} {...LIME_FOCUS} />;

      case FIELD_TYPES.TEXTAREA:
        return <Input as="textarea" minH="80px" py={2} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} {...LIME_FOCUS} />;

      case FIELD_TYPES.NUMBER:
        return unit ? (
          <InputGroup>
            <InputLeftAddon>{unit}</InputLeftAddon>
            <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} {...LIME_FOCUS} />
          </InputGroup>
        ) : (
          <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} {...LIME_FOCUS} />
        );

      case FIELD_TYPES.SELECT:
        return (
          <Select value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Select…" {...LIME_FOCUS}>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </Select>
        );

      case FIELD_TYPES.RADIO:
        return (
          <RadioGroup value={value || ""} onChange={onChange}>
            <HStack spacing={5}>
              {options.map((o) => <Radio key={o} value={o} colorScheme="limeShade">{o}</Radio>)}
            </HStack>
          </RadioGroup>
        );

      case FIELD_TYPES.CHECKBOX_GROUP:
        return (
          <CheckboxGroup value={value || []} onChange={onChange}>
            {columns > 1 ? (
              <SimpleGrid columns={{ base: 1, md: columns }} spacingY={2.5} spacingX={8}>
                {options.map((o) => <Checkbox key={o} value={o} colorScheme="limeShade">{o}</Checkbox>)}
              </SimpleGrid>
            ) : (
              <HStack spacing={5} flexWrap="wrap">
                {options.map((o) => <Checkbox key={o} value={o} colorScheme="limeShade">{o}</Checkbox>)}
              </HStack>
            )}
          </CheckboxGroup>
        );

      case FIELD_TYPES.CONFIRM:
        return (
          <Checkbox isChecked={!!value} onChange={(e) => onChange(e.target.checked)} colorScheme="limeShade" alignItems="flex-start">
            <Text fontSize="14px" color="gray.700">{checkboxLabel || "I confirm"}</Text>
          </Checkbox>
        );

      case FIELD_TYPES.RICH_TEXT:
        return (
          <RichTextEditor content={value || ""} onChange={onChange} height="220px" />
        );

      default:
        return null;
    }
  })();

  // The confirm field renders its own (reminder) label as body text above the
  // checkbox, so it doesn't use the standard bold FormLabel.
  if (type === FIELD_TYPES.CONFIRM) {
    return (
      <FormControl isRequired={required}>
        <Text fontSize="13px" color="gray.600" mb={2}>{label}</Text>
        {control}
      </FormControl>
    );
  }

  return (
    <FormControl isRequired={required}>
      <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={1.5}>
        {label}
        {unit && type !== FIELD_TYPES.NUMBER && <Text as="span" color="gray.400" fontWeight="400"> ({unit})</Text>}
      </FormLabel>
      <Box maxW={controlMaxW}>{control}</Box>
      {help && <FormHelperText fontSize="xs" color="gray.500">{help}</FormHelperText>}
    </FormControl>
  );
}

// Renders a schema (array of sections) into a stacked form.
export function TaskFieldSections({ schema, values, onFieldChange }) {
  return (
    <VStack align="stretch" spacing={8}>
      {schema.map((section) => (
        <VStack key={section.section} align="stretch" spacing={4}>
          <VStack align="stretch" spacing={0.5}>
            <Text fontSize="body.l" fontWeight="700" color="darkTeal">{section.section}</Text>
            {section.description && <Text fontSize="body.xs" color="gray.500">{section.description}</Text>}
          </VStack>
          <Flex gap={3} flexWrap="wrap">
            <SectionAction icon={<FileArrowUp size={15} />}>Import from Template</SectionAction>
            <SectionAction icon={<Sparkle size={15} weight="fill" />}>AI Mapping</SectionAction>
          </Flex>
          {section.fields.map((field) => (
            <TaskField
              key={field.key}
              field={field}
              value={values[field.key]}
              onChange={(v) => onFieldChange(field.key, v)}
            />
          ))}
        </VStack>
      ))}
    </VStack>
  );
}

export default TaskField;
